import React, {useEffect, useState, forwardRef, useImperativeHandle} from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import {useLocation, useNavigate} from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import FolderIcon from "@mui/icons-material/Folder";
import "./DriverFolder.css";
import { motion } from "framer-motion";
import {useCustomAuth} from "../contexts/AuthContext"; // Compatible with v4.1.17
import FileTable from "./FileTable"; // Import the new FileTable component
import {ANNOTATION_SESSION_ENDPOINT, AWS_LAMBDA_ENDPOINT, ALB_API_BASE_URL} from "../utils/constants";
import OptionsMenu from "./OptionsMenu";
import { authFetch } from "../utils/authFetch";
import LoadingModal from "./LoadingModal";
import {
    addDirectAnnotator,
    normalizeAnnotatorAssignment,
    removeDirectAnnotator,
} from "../utils/annotatorAssignment";
import { buildAnnotationSheetLocation } from "../utils/annotationSheetNavigation";

async function fetchSheetMeta(sheetIds, userId) {
    if (!sheetIds || sheetIds.length === 0) {
        console.warn("No sheetIds provided for metadata fetch");
        return [];
    }
    const metaRsp = await authFetch(`${ANNOTATION_SESSION_ENDPOINT}/batchGetAnnotationMetadata`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sheet_ids: sheetIds, user_id: userId }),
      });
      const metas = await metaRsp.json();    // array of metadata
      return metas.map((meta) => ({
          ...meta,
          assigned_annotators: normalizeAnnotatorAssignment(
              meta.assigned_annotators,
              `assigned_annotators for ${meta.sheet_id}`
          ),
      }));
}

class Node {
    constructor() {
        this.children = {};
        this.sheet_id = null;
        this.sheet_data = null;
        this.folder_annotators = { parent: [], current: [] };
    }
}

function insert(path, sheet_id, root, sheet_data = null) {
    // drop any leading/trailing slashes so we get ["neuromuse","oscar","song1.mid"]
    const clean = path.replace(/^\/+|\/+$/g, "");
    const folders = clean.split("/");
    let ptr = root;
    for (const name of folders) {
        if (!ptr.children[name]) {
            ptr.children[name] = new Node();
        }
        ptr = ptr.children[name];
    }
    ptr.sheet_id = sheet_id;
    ptr.sheet_data = sheet_data;
}

// turn the nested JSON children[] into trie (Admin mode)
function buildTrieFromApi(childrenArray) {
    const root = new Node();
    function walk(arr, ptr) {
      for (const item of arr) {
        const { type, name, sheetId, children } = item;
        if (type === "folder") {
          if (!ptr.children[name]) ptr.children[name] = new Node();
          walk(children, ptr.children[name]);
        } else if (type === "file") {
          ptr.children[name] = new Node();
          ptr.children[name].sheet_id   = sheetId;
          ptr.children[name].sheet_data = item;
        }
      }
    }
    walk(childrenArray, root);
    return root;
}

const DriveFolder = forwardRef(({ onPathChange, searchTerm, isAdminMode, setRootNode: setRootNodeExternal, refreshToggle }, ref) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const { userData, allUsers, setIsAdminMode } = useCustomAuth();
    const location = useLocation();
    const isAdmin = userData?.roles.includes("ADMIN") || userData?.roles.includes("ROOT") || false

    const [rootNode, setRootNode] = useState(null);
    const [path, setPath] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortConfig, setSortConfig] = useState({
        key: 'filename',
        direction: 'asc'
    });
    const [actionLoading, setActionLoading] = useState(false);
    const [actionMessage, setActionMessage] = useState("");

    const handleAnnotatorsAdd = async (node, addedUser) => {
        const { sheet_id, sheet_data } = node;
        const filePath = sheet_data.file_path;

        await authFetch(`${AWS_LAMBDA_ENDPOINT}/assignUsers`, {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sheet_id: sheet_id,
            file_path: filePath,
            users: [{
              username: addedUser.preferred_username || addedUser.email.split("@")[0],
              email:    addedUser.email,
              name:     addedUser.name
            }]
          })
        });

        node.sheet_data.assigned_annotators = addDirectAnnotator(
          node.sheet_data.assigned_annotators,
          addedUser
        );

        node.sheet_data.last_updated = new Date().toISOString();

        // shallow‐clone rootNode to force React to notice
        setRootNode(r => ({ ...r }));
    };

    const handleAnnotatorsRemove = async (node, removedUser) => {
        const { file_path } = node.sheet_data;

        const res = await authFetch(`${AWS_LAMBDA_ENDPOINT}/unassignUsers`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sheet_id: node.sheet_id,
            file_path,
            users: [{
              username: removedUser.preferred_username || removedUser.email.split("@")[0],
              email:    removedUser.email,
              name:     removedUser.name
            }]
          })
        });
        if (!res.ok) {
          console.error("removeUsers failed:", await res.text());
          return;
        }

        node.sheet_data.assigned_annotators = removeDirectAnnotator(
          node.sheet_data.assigned_annotators,
          removedUser.email
        );

        node.sheet_data.last_updated = new Date().toISOString();

        setRootNode(r => ({ ...r }));
    };

    // Handle folder annotator changes
    const handleFolderAnnotatorsAdd = async (node, addedUser, folderPath) => {
        try {
            await authFetch(`${AWS_LAMBDA_ENDPOINT}/assignUsersToFolder`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    folder_path: folderPath,
                    users: [{
                        username: addedUser.preferred_username || addedUser.email.split("@")[0],
                        email: addedUser.email,
                        name: addedUser.name
                    }]
                })
            });

            node.folder_annotators = addDirectAnnotator(node.folder_annotators, addedUser);

            // Force re-render
            setRootNode(r => ({ ...r }));
        } catch (err) {
            console.error("Error adding folder annotator:", err);
        }
    };

    const handleFolderAnnotatorsRemove = async (node, removedUser, folderPath) => {
        try {
            await authFetch(`${AWS_LAMBDA_ENDPOINT}/unassignUsersToFolder`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    folder_path: folderPath,
                    users: [{
                        username: removedUser.preferred_username || removedUser.email.split("@")[0],
                        email: removedUser.email,
                        name: removedUser.name
                    }]
                })
            });

            node.folder_annotators = removeDirectAnnotator(
                node.folder_annotators,
                removedUser.email
            );

            // Force re-render
            setRootNode(r => ({ ...r }));
        } catch (err) {
            console.error("Error removing folder annotator:", err);
        }
    };

    // Helper function to calculate folder path from trie structure
    const calculateFolderPath = (targetNode) => {
        const findPath = (node, currentPath = []) => {
            if (node === targetNode) {
                return currentPath;
            }
            
            for (const [name, child] of Object.entries(node.children || {})) {
                if (child === targetNode) {
                    return [...currentPath, name];
                }
                
                const result = findPath(child, [...currentPath, name]);
                if (result) {
                    return result;
                }
            }
            
            return null;
        };
        
        const pathArray = findPath(rootNode);
        return pathArray ? pathArray.join('/') : '';
    };

    const handleAnnotatorsChange = (node, user, isAdd) => {
        // Check if this is a folder or file
        if (node.sheet_id) {
            // This is a file
            return isAdd
                ? handleAnnotatorsAdd(node, user)
                : handleAnnotatorsRemove(node, user);
        } else {
            // This is a folder - calculate the actual folder path
            const folderPath = calculateFolderPath(node);
            return isAdd
                ? handleFolderAnnotatorsAdd(node, user, folderPath)
                : handleFolderAnnotatorsRemove(node, user, folderPath);
        }
    };


    // Separate sort state for Recently Used Files
    const [recentFilesSortConfig, setRecentFilesSortConfig] = useState({
        key: 'updated',
        direction: 'asc'
    });

    // Search results
    const [searchResults, setSearchResults] = useState([]);

    // Function to collect matching search results
    const collectMatches = (node, path = []) => {
        let results = [];
        for (const [name, child] of Object.entries(node.children)) {
            const fullPath = [...path, name];
            // Case-insensitive
            if (name.toLowerCase().includes(searchTerm.toLowerCase())) {
                results.push({
                    name,
                    path: fullPath,
                    sheet_id: child.sheet_id,
                    // Create a unique ID to avoid duplicates
                    unique_id: fullPath.join('/') + (child.sheet_id || '')
                });
            }

            // Recursive call
            results = results.concat(collectMatches(child, fullPath));
        }
        return results;
    };

    // Expose resetPath method to parent component
    useImperativeHandle(ref, () => ({
        resetPath: () => {
            setPath([]);
        },
        getRootNode: () => rootNode
    }));

    // Search function
    useEffect(() => {
        if (searchTerm && rootNode) {
            setSearchResults(collectMatches(rootNode));
        } else {
            setSearchResults([]);
        }
    }, [searchTerm, rootNode]);


// Helper: collect all sheetIds from the trie
function collectSheetIds(root) {
    const ids = [];
    function recurse(node) {
      if (node.sheet_id) ids.push(node.sheet_id);
      if (node.children) {
        Object.values(node.children).forEach(recurse);
      }
    }
    recurse(root);
    return ids;
  }

  // Helper: attach metadata from metaMap into each trie node
  function attachMetadata(root, metaMap) {
    function recurse(node) {
      if (node.sheet_id && metaMap[node.sheet_id]) {
        node.sheet_data = metaMap[node.sheet_id];
      }
      if (node.children) {
        Object.values(node.children).forEach(recurse);
      }
    }
    recurse(root);
  }

  // Helper: collect all folder paths from the trie
  function collectAllFolderPaths(root) {
    const paths = [];
    function recurse(node, currentPath = []) {
      if (currentPath.length > 0) {
        paths.push(currentPath.join('/'));
      }
      if (node.children) {
        Object.entries(node.children).forEach(([name, child]) => {
          if (!child.sheet_id) { // Only folders, not files
            recurse(child, [...currentPath, name]);
          }
        });
      }
    }
    recurse(root);
    return paths;
  }

  // Helper: attach folder annotators to trie nodes
  function attachFolderAnnotators(root, folderMetaArray) {
    const folderMetaMap = Object.fromEntries(
      folderMetaArray.map(meta => [meta.folder_path, meta.assigned_annotators])
    );

    function recurse(node, currentPath = []) {
      if (currentPath.length > 0) {
        const folderPath = currentPath.join('/');
        if (folderMetaMap[folderPath]) {
          node.folder_annotators = normalizeAnnotatorAssignment(
            folderMetaMap[folderPath],
            `assigned_annotators for ${folderPath}`
          );
        }
      }
      if (node.children) {
        Object.entries(node.children).forEach(([name, child]) => {
          if (!child.sheet_id) { // Only folders, not files
            recurse(child, [...currentPath, name]);
          }
        });
      }
    }
    recurse(root);
  }

  // Helper: load folder annotators from backend
  async function loadFolderAnnotators(root) {
    try {
      const folderPaths = collectAllFolderPaths(root);
      if (folderPaths.length === 0) return;

      const response = await authFetch(`${ANNOTATION_SESSION_ENDPOINT}/batchGetFolderMetadata`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ folder_paths: folderPaths })
      });

      if (response.ok) {
        const folderMetaArray = await response.json();
        attachFolderAnnotators(root, folderMetaArray);
      }
    } catch (err) {
      console.error("Error loading folder annotators:", err);
    }
  }

  useEffect(() => {
    async function load() {
    //   console.log("DriveFolder load: adminMode=", isAdminMode, "userData=", userData);
      setLoading(true);

      if (!userData) {
        // console.log("No userData; clearing tree");
        setRootNode(new Node());
        if (setRootNodeExternal) setRootNodeExternal(new Node());
        setLoading(false);
        return;
      }

      if (isAdminMode) {
        try {
        //   console.log("Fetching global folder structure...");
          const resp = await authFetch(
            `${AWS_LAMBDA_ENDPOINT}/getGlobalStructure?user_id=${encodeURIComponent(userData.user_id)}`
          );
          if (!resp.ok) throw new Error("Failed to fetch global structure");
          const { children } = await resp.json();
        //   console.log("Folder JSON:", children);

          // Build trie
          const root = buildTrieFromApi(children);
        //   console.log("Trie built");

          // Collect sheet IDs
          const sheetIds = collectSheetIds(root);
        //   console.log("Collected sheetIds:", sheetIds);

          // fetch metadata for all sheets
          const metas = await fetchSheetMeta(sheetIds, userData.user_id);
          const metaMap = Object.fromEntries(metas.map(m => [m.sheet_id, m]));

          // Attach metadata to trie
          attachMetadata(root, metaMap);
        //   console.log("Metadata attached to trie nodes");

          // Load folder annotators
          await loadFolderAnnotators(root);
        //   console.log("Folder annotators loaded");

          setRootNode(root);
          if (setRootNodeExternal) setRootNodeExternal(root);
        } catch (err) {
        //   console.error("Admin load failed:", err);
          setRootNode(new Node());
          if (setRootNodeExternal) setRootNodeExternal(new Node());
        } finally {
          setLoading(false);
        //   console.log("Admin load complete");
        }
      } else {
        // User mode: insert assigned_sheets and load folder annotators
        // console.log("Loading user-mode sheets");
        const root = new Node();
        (userData.assigned_sheets || []).forEach(sheet => {
          insert(sheet.path, sheet.sheet_id, root, sheet.sheet_data);
        });

        // Load folder annotators for user mode
        try {
          await loadFolderAnnotators(root);
          // console.log("User-mode folder annotators loaded");
        } catch (err) {
          console.error("Error loading folder annotators in user mode:", err);
        }

        setRootNode(root);
        if (setRootNodeExternal) setRootNodeExternal(root);
        setLoading(false);
        // console.log("User-mode load complete");
      }
    }

    load();
  }, [userData, isAdminMode, refreshToggle]);

    // Notify parent component about path changes
    useEffect(() => {
        if (onPathChange) {
            onPathChange(path);
        }
    }, [path, onPathChange]);

    // if clicked back on AnnotationSheet page, direct back to correct folder
    useEffect(() => {
        const qp = new URLSearchParams(location.search);
        const raw = qp.get("path") || "";
        if (raw) {
            const segments = raw.split("/").filter(s => s.length > 0);
            setPath(segments);
            // We leave searchTerm alone; if someone came back with ?path=..., we assume no search
        }
    }, [location.search]);

    // New function to collect all files from all folders with path information
    const getAllFiles = () => {
        if (!rootNode) return [];

        const allFiles = [];

        // Recursive function to traverse the tree
        const collectFiles = (node, path) => {
            Object.entries(node.children).forEach(([key, childNode]) => {
                if (childNode.sheet_id) {
                    // This is a file - store it with path information
                    const fileWithPath = [key, childNode, [...path, key]]; // Add path as third element
                    allFiles.push(fileWithPath);
                } else {
                    // This is a folder, recursively collect its files
                    collectFiles(childNode, [...path, key]);
                }
            });
        };

        collectFiles(rootNode, []);

        // Limit to 5 files (threshold)
        return allFiles.slice(0, 5);
    };

    const traverseToCurrentNode = () => {
        if (!rootNode) return null;
        let ptr = rootNode;
        for (const folder of path) {
            if (!ptr.children[folder]) return null;
            ptr = ptr.children[folder];
        }
        return ptr;
    };

    const handleFolderClick = (folderName) => {
        setPath([...path, folderName]);
    };

    const handleSheetClick = (sheetId, view = "edit", containingPath = path) => {
        navigate(buildAnnotationSheetLocation({
            sheetId,
            mode: view,
            folderPath: containingPath,
        }));
    };

    // Delete a sheet by sheetId
    const handleSheetDelete = async (sheetId) => {
        if (!sheetId) return;
        setActionMessage("Deleting sheet… (正在删除文件...)");
        setActionLoading(true);
        try {
            const res = await authFetch(`${AWS_LAMBDA_ENDPOINT}/batchDeleteAnnotationSheet`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sheetIds: [sheetId] })
            });
            const payload = await res.json();
            if (!res.ok) {
                console.error("batchDeleteAnnotationSheet error:", payload);
                setActionLoading(false);
                return;
            }

            function cloneNode(node) {
                const copy = new Node();
                copy.sheet_id   = node.sheet_id;
                copy.sheet_data = node.sheet_data;
                for (const [name, child] of Object.entries(node.children)) {
                    copy.children[name] = cloneNode(child);
                }
                return copy;
            }

            function removeFromTrie(node, sheetId) {
                for (const [name, child] of Object.entries(node.children)) {
                    if (child.sheet_id === sheetId) {
                        delete node.children[name];
                        return true;
                    }
                    if (removeFromTrie(child, sheetId)) {
                        return true;
                    }
                }
                return false;
            }
            // locally patch the trie
            setRootNode(prev => {
                const newRoot = cloneNode(prev);
                removeFromTrie(newRoot, sheetId);
                return newRoot;
            });

        } catch (err) {
            console.error("Error in handleDelete:", err);
        } finally {
            setActionLoading(false);
        }
    };

    // Rename a sheet by sheetId
    const handleSheetRename = async (sheetId, newName) => {
        if (!sheetId || !newName) return;
        setActionMessage("Renaming sheet… (正在重命名文件...)");
        setActionLoading(true);
        try {
        // 1) Call the renameAnnotationSheet Lambda
        const res = await authFetch(
            `${AWS_LAMBDA_ENDPOINT}/renameAnnotationSheet`,
            {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ sheetId, newName })
            }
        );
        const payload = await res.json();
        if (!res.ok) {
            console.error("renameAnnotationSheet error:", payload);
            setActionLoading(false);
            return;
        }
        // console.log("Renamed sheet:", payload);

        // 2) Update the trie in state so the UI reflects the new displayed_filename
        setRootNode(prev => {
            // deep‐clone just the children tree
            const newRoot = { children: JSON.parse(JSON.stringify(prev.children)) };
            // recursive helper to find and patch the node
            const patch = node => {
            if (node.sheet_id === sheetId) {
                node.sheet_data = node.sheet_data || {};
                node.sheet_data.displayed_filename = newName;
            }
            Object.values(node.children || {}).forEach(patch);
            };
            patch(newRoot);
            return newRoot;
        });

        } catch (err) {
            console.error("Error in handleRename:", err);
        } finally {
            setActionLoading(false);
        }
    };

    // Function to get sorted recent files
    const getSortedRecentFiles = () => {
        let allFiles = getAllFiles();
        if (!recentFilesSortConfig.key) return allFiles;

        return [...allFiles].sort((a, b) => {
            // First item is [key, node]
            const keyA = a[0].toLowerCase();
            const keyB = b[0].toLowerCase();

            // Different sort logic based on column
            if (recentFilesSortConfig.key === 'filename') {
                const fileNameA = a[1].sheet_data?.displayed_filename || keyA;
                const fileNameB = b[1].sheet_data?.displayed_filename || keyB;
                if (recentFilesSortConfig.direction === 'asc') {
                    return fileNameA.toLowerCase() > fileNameB.toLowerCase() ? 1 : -1;
                } else {
                    return fileNameA.toLowerCase() < fileNameB.toLowerCase() ? 1 : -1;
                }
            }
            else if (recentFilesSortConfig.key === 'owner') {
                // Self-Created first, assigned next
                const ownerA = a[1].sheet_data?.displayed_owner || '-';
                const ownerB = b[1].sheet_data?.displayed_owner || '-';

                if (ownerA === ownerB) {
                    // If owners are the same, sort by filename
                    return keyA > keyB ? 1 : -1;
                }

                // Non-dash values come first
                if (recentFilesSortConfig.direction === 'asc') {
                    return ownerA === '-' ? 1 : -1;
                } else {
                    return ownerA === '-' ? -1 : 1;
                }
            }
            else if (recentFilesSortConfig.key === 'updated') {
                // Get real date values from sheet_data
                const dateA = a[1].sheet_data?.last_updated ? new Date(a[1].sheet_data.last_updated).getTime() : 0;
                const dateB = b[1].sheet_data?.last_updated ? new Date(b[1].sheet_data.last_updated).getTime() : 0;

                // If dates are equal, sort by filename alphabetically
                if (dateA === dateB) {
                    return keyA > keyB ? 1 : -1;
                }

                // More recent dates first
                if (recentFilesSortConfig.direction === 'asc') {
                    return dateA > dateB ? -1 : 1;
                } else {
                    return dateA > dateB ? 1 : -1;
                }
            }
            else if (recentFilesSortConfig.key === 'status') {
                // Map statuses to priority values (Ongoing:0, Not Started:1, Completed:2)
                const getStatusPriority = (status) => {
                    if (!status || status === '-') return 3; // Default to lowest priority
                    const statusLower = status.toLowerCase();
                    if (statusLower.includes('progress') || statusLower.includes('ongoing')) return 0;
                    if (statusLower.includes('not') || statusLower.includes('start')) return 1;
                    if (statusLower.includes('complete') || statusLower.includes('done')) return 2;
                    return 3; // Default to lowest priority for unknown status
                };

                const statusA = getStatusPriority(a[1].sheet_data?.status);
                const statusB = getStatusPriority(b[1].sheet_data?.status);

                // If statuses are equal, sort by filename
                if (statusA === statusB) {
                    return keyA > keyB ? 1 : -1;
                }

                // Sort by status priority (Ongoing, Not Started, Completed, -)
                if (recentFilesSortConfig.direction === 'asc') {
                    return statusA > statusB ? 1 : -1;
                } else {
                    return statusA < statusB ? 1 : -1;
                }
            }

            return 0;
        });
    };

    const handleFolderRename = async (node, newName) => {
        const oldPath = calculateFolderPath(node);
        console.log("Trying to rename", oldPath, "to", newName);
        setActionMessage("Renaming folder... (正在重命名文件夹...)");
        setActionLoading(true);
        if (!oldPath) return;

        try {
            const res = await authFetch(
            `${AWS_LAMBDA_ENDPOINT}/renameFolder`,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ folderPath: oldPath, newName: newName })
            }
            );
            const payload = await res.json();
            if (!res.ok) {
                console.error("renameFolder error:", payload);
                setActionLoading(false);
                return;
            }
        }
        catch (err) {
            console.error("Error in handleFolderRename:", err);
            setActionLoading(false);
            return;
        }

        // 3) update the trie in‐memory
        const segments = oldPath.split('/');
        const oldName = segments.pop();          // the last segment
        // 3) walk to its parent in the trie
        let parent = rootNode;
        for (const seg of segments) {
        parent = parent.children[seg];
        }
        // 4) rebuild `children` preserving original order
        const newChildren = {};
        for (const [key, childNode] of Object.entries(parent.children)) {
        if (key === oldName) {
            newChildren[newName] = childNode;  // swap in the renamed key
        } else {
            newChildren[key] = childNode;      // preserve everything else
        }
        }
        parent.children = newChildren;
        // 5) trigger React to re-render
        setRootNode(r => ({ ...r }));
        setActionLoading(false);
    }

    const handleFolderDelete = async (node) => {
        const folderPath = calculateFolderPath(node);
        if (!folderPath) return;
        setActionMessage("Deleting folder... (正在删除文件夹...)");
        setActionLoading(true);
        console.log("Trying to delete", folderPath);

        try {
            const res = await authFetch(
            `${ALB_API_BASE_URL}/batchDeleteFolder`,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ folders: [folderPath] })
            }
            );
            const payload = await res.json();
            if (!res.ok) {
                console.error("batchDeleteFolder error:", payload);
                setActionLoading(false);
                return;
            }
        }
        catch (err) {
            console.error("Error in handleFolderDelete:", err);
            setActionLoading(false);
            return;
        }
        
        const segments = folderPath.split('/');
        // Then update local state:
        setRootNode(prev => {
            // shallow clone only forces React to re‑render; we can mutate in place
            const rootCopy = { ...prev, children: { ...prev.children } };
            // walk and delete
            let ptr = rootCopy;
            for (let i = 0; i < segments.length - 1; i++) {
            const seg = segments[i];
            ptr.children[seg] = { 
                ...ptr.children[seg], 
                children: { ...ptr.children[seg].children } 
            };
            ptr = ptr.children[seg];
            }
            delete ptr.children[segments[segments.length - 1]];
            return rootCopy;
        });
        setActionLoading(false);
    }

    function getFileCount(node) {
        if (!node) return 0;
        // Only count those with a sheet id
        let count = Object.values(node.children || {}).filter(child => child.sheet_id).length;
        for (const child of Object.values(node.children || {})) {
            count += getFileCount(child);
        }
        return count;
    }

    function getFolderCount(node) {
        if (!node) return 0;
        // Only count those without a sheet id
        let count = Object.values(node.children || {}).filter(child => !child.sheet_id).length;

        for (const child of Object.values(node.children || {})) {
            count += getFolderCount(child);
        }

        return count;
    }

    const currentNode = traverseToCurrentNode();
    const fileCount = currentNode
    ? Object.values(currentNode.children || {}).filter(child => child.sheet_id).length
    : 0;
    const totalFileCount = getFileCount(currentNode);
    const folderCount = currentNode
    ? Object.values(currentNode.children || {}).filter(child => !child.sheet_id).length
    : 0;
    const totalFolderCount = getFolderCount(currentNode);

    // Determine if we're inside a folder or at root level
    const isInsideFolder = path.length > 0;

    // Handle sort when clicking on table headers
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Function to get sorted items
    const getSortedItems = (items) => {
        if (!sortConfig.key) return items;

        return [...items].sort((a, b) => {
            // First item is [key, node]
            const keyA = a[0].toLowerCase();
            const keyB = b[0].toLowerCase();

            // Different sort logic based on column
            if (sortConfig.key === 'filename') {
                const fileNameA = a[1].sheet_data?.displayed_filename || keyA;
                const fileNameB = b[1].sheet_data?.displayed_filename || keyB;
                if (sortConfig.direction === 'asc') {
                    return fileNameA.toLowerCase() > fileNameB.toLowerCase() ? 1 : -1;
                } else {
                    return fileNameA.toLowerCase() < fileNameB.toLowerCase() ? 1 : -1;
                }
            }
            else if (sortConfig.key === 'owner') {
                // Self-Created first, assigned next
                const ownerA = a[1].sheet_data?.displayed_owner || '-';
                const ownerB = b[1].sheet_data?.displayed_owner || '-';

                if (ownerA === ownerB) {
                    // If owners are the same, sort by filename
                    return keyA > keyB ? 1 : -1;
                }

                // Non-dash values come first
                if (sortConfig.direction === 'asc') {
                    return ownerA === '-' ? 1 : -1;
                } else {
                    return ownerA === '-' ? -1 : 1;
                }
            }
            else if (sortConfig.key === 'updated') {
                // Get real date values from sheet_data
                const dateA = a[1].sheet_data?.last_updated ? new Date(a[1].sheet_data.last_updated).getTime() : 0;
                const dateB = b[1].sheet_data?.last_updated ? new Date(b[1].sheet_data.last_updated).getTime() : 0;

                // If dates are equal, sort by filename alphabetically
                if (dateA === dateB) {
                    return keyA > keyB ? 1 : -1;
                }

                // More recent dates first
                if (sortConfig.direction === 'asc') {
                    return dateA > dateB ? -1 : 1;
                } else {
                    return dateA > dateB ? 1 : -1;
                }
            }
            else if (sortConfig.key === 'status') {
                // Map statuses to priority values (Ongoing:0, Not Started:1, Completed:2)
                const getStatusPriority = (status) => {
                    if (!status || status === '-') return 3; // Default to lowest priority
                    const statusLower = status.toLowerCase();
                    if (statusLower.includes('progress') || statusLower.includes('ongoing')) return 0;
                    if (statusLower.includes('not') || statusLower.includes('start')) return 1;
                    if (statusLower.includes('complete') || statusLower.includes('done')) return 2;
                    return 3; // Default to lowest priority for unknown status
                };

                const statusA = getStatusPriority(a[1].sheet_data?.status);
                const statusB = getStatusPriority(b[1].sheet_data?.status);

                // If statuses are equal, sort by filename
                if (statusA === statusB) {
                    return keyA > keyB ? 1 : -1;
                }

                // Sort by status priority (Ongoing, Not Started, Completed, -)
                if (sortConfig.direction === 'asc') {
                    return statusA > statusB ? 1 : -1;
                } else {
                    return statusA < statusB ? 1 : -1;
                }
            }

            return 0;
        });
    };

    // Render search results
    const renderSearchResults = () => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (searchResults.length === 0) {
            return (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                    <Typography variant="body1" color="text.secondary">
                        No results found for "{searchTerm}"
                    </Typography>
                </Box>
            );
        }

        // Generate a unique ID for each result based on its full path
        const uniqueResults = Array.from(
            new Map(searchResults.map(item => {
                const uniqueId = item.path.join('/') + (item.sheet_id || '');
                return [uniqueId, item];
            })).values()
        );

        // Create a direct mapping for the FileTable - use uniqueId as key
        const searchItems = [];
        const pathsMap = {};
        const folderPathsMap = {};
        const displayNames = {};

        uniqueResults.forEach((result, index) => {
            let node = rootNode;
            for (const segment of result.path) {
                node = node.children[segment];
            }

            // Use index as the stable key to avoid duplication issues
            const stableKey = `search-result-${index}`;
            searchItems.push([stableKey, node]);
            pathsMap[stableKey] = result.path.join(' / ');
            folderPathsMap[stableKey] = result.path.slice(0, -1);

            // Store the actual file/folder name for display
            displayNames[stableKey] = node.sheet_data?.displayed_filename || result.name;
        });

        // FOLDER-FIRST, ALPHABETIZED SORT for search results
        searchItems.sort((a, b) => {
            const nodeA = a[1];
            const nodeB = b[1];
            const isFolderA = !nodeA.sheet_id;
            const isFolderB = !nodeB.sheet_id;
            if (isFolderA !== isFolderB) {
                return isFolderA ? -1 : 1;
            }
            const nameA = displayNames[a[0]] || '';
            const nameB = displayNames[b[0]] || '';
            return nameA.localeCompare(nameB);
        });

        // Special sort function just for search results
        const sortSearchItems = (items) => {
            if (!sortConfig.key || sortConfig.key === 'filename') {
                // FOLDER-FIRST, ALPHABETIZED SORT for search results
                return [...items].sort((a, b) => {
                    const nodeA = a[1];
                    const nodeB = b[1];
                    const isFolderA = !nodeA.sheet_id;
                    const isFolderB = !nodeB.sheet_id;
                    if (isFolderA !== isFolderB) {
                        return isFolderA ? -1 : 1;
                    }
                    const nameA = displayNames[a[0]] || '';
                    const nameB = displayNames[b[0]] || '';
                    return nameA.localeCompare(nameB);
                });
            }
            if (!sortConfig.key) return items;

            return [...items].sort((a, b) => {
                // Items are [stableKey, node]
                const nodeA = a[1];
                const nodeB = b[1];
                const keyA = a[0];
                const keyB = b[0];

                // Different sort logic based on column
                if (sortConfig.key === 'filename') {
                    const fileNameA = displayNames[keyA] || nodeA.sheet_data?.displayed_filename || keyA;
                    const fileNameB = displayNames[keyB] || nodeB.sheet_data?.displayed_filename || keyB;
                    if (sortConfig.direction === 'asc') {
                        return fileNameA.toLowerCase() > fileNameB.toLowerCase() ? 1 : -1;
                    } else {
                        return fileNameA.toLowerCase() < fileNameB.toLowerCase() ? 1 : -1;
                    }
                }
                else if (sortConfig.key === 'owner') {
                    const ownerA = nodeA.sheet_data?.displayed_owner || '-';
                    const ownerB = nodeB.sheet_data?.displayed_owner || '-';

                    if (ownerA === ownerB) {
                        // If owners are the same, sort by filename
                        const fileNameA = displayNames[keyA] || nodeA.sheet_data?.displayed_filename || keyA;
                        const fileNameB = displayNames[keyB] || nodeB.sheet_data?.displayed_filename || keyB;
                        return fileNameA.toLowerCase() > fileNameB.toLowerCase() ? 1 : -1;
                    }

                    if (sortConfig.direction === 'asc') {
                        return ownerA === '-' ? 1 : -1;
                    } else {
                        return ownerA === '-' ? -1 : 1;
                    }
                }
                else if (sortConfig.key === 'updated') {
                    const dateA = nodeA.sheet_data?.last_updated ? new Date(nodeA.sheet_data.last_updated).getTime() : 0;
                    const dateB = nodeB.sheet_data?.last_updated ? new Date(nodeB.sheet_data.last_updated).getTime() : 0;

                    if (dateA === dateB) {
                        const fileNameA = displayNames[keyA] || nodeA.sheet_data?.displayed_filename || keyA;
                        const fileNameB = displayNames[keyB] || nodeB.sheet_data?.displayed_filename || keyB;
                        return fileNameA.toLowerCase() > fileNameB.toLowerCase() ? 1 : -1;
                    }

                    if (sortConfig.direction === 'asc') {
                        return dateA > dateB ? -1 : 1;
                    } else {
                        return dateA > dateB ? 1 : -1;
                    }
                }
                else if (sortConfig.key === 'status') {
                    const getStatusPriority = (status) => {
                        if (!status || status === '-') return 3;
                        const statusLower = status.toLowerCase();
                        if (statusLower.includes('progress') || statusLower.includes('ongoing')) return 0;
                        if (statusLower.includes('not') || statusLower.includes('start')) return 1;
                        if (statusLower.includes('complete') || statusLower.includes('done')) return 2;
                        return 3;
                    };

                    const statusA = getStatusPriority(nodeA.sheet_data?.status);
                    const statusB = getStatusPriority(nodeB.sheet_data?.status);

                    if (statusA === statusB) {
                        const fileNameA = displayNames[keyA] || nodeA.sheet_data?.displayed_filename || keyA;
                        const fileNameB = displayNames[keyB] || nodeB.sheet_data?.displayed_filename || keyB;
                        return fileNameA.toLowerCase() > fileNameB.toLowerCase() ? 1 : -1;
                    }

                    if (sortConfig.direction === 'asc') {
                        return statusA > statusB ? 1 : -1;
                    } else {
                        return statusA < statusB ? 1 : -1;
                    }
                }

                return 0;
            });
        };

        const onSearchFolderClick = (stableKey) => {
            const realPath = pathsMap[stableKey].split(" / ");
            setPath(realPath);
            onPathChange?.(realPath);
        };

        return (
          <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
            <FileTable
              items={sortSearchItems(searchItems)}
              sortConfig={sortConfig}
              handleSort={handleSort}
              handleFolderClick={onSearchFolderClick}
              handleSheetClick={handleSheetClick}
              showPath={true}
              itemPaths={pathsMap}
              itemFolderPaths={folderPathsMap}
              displayNames={displayNames}
              allUsers={allUsers}
              onAnnotatorsChange={handleAnnotatorsChange}
              handleSheetDelete={handleSheetDelete}
              handleSheetRename={handleSheetRename}
              handleFolderDelete={handleFolderDelete}
              handleFolderRename={handleFolderRename}
              rootNode={rootNode}
              currentPath={[]}
            />
          </Box>
        );
    };

    // Render folders at root level
    const renderFoldersGrid = () => {
        return (
            <Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 5 }}>
                    {currentNode &&
                        Object.entries(currentNode.children).map(([key, node]) => (
                            <Box
                                key={key}
                                sx={{
                                    width: { xs: "100%", sm: 250 },
                                    borderRadius: 1,
                                    border: "1px solid #e0e0e0",
                                    p: 2,
                                    cursor: "pointer",
                                    "&:hover": {
                                        bgcolor: "#f5f5f5",
                                    },
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between"
                                }}
                                onClick={() => handleFolderClick(key)}
                            >
                              <Box sx={{ display: "flex", alignItems: "center" }}>
                                <FolderIcon sx={{ mr: 2 }} />
                                <Typography sx={{ fontWeight: "medium" }}>
                                  {key}
                                </Typography>
                              </Box>

                              {(isAdminMode && isAdmin) && (
                                <OptionsMenu
                                  onDelete={() => handleFolderDelete(node)}
                                  onRename={(newName) => handleFolderRename(node, newName)}
                                  annotators={node.folder_annotators.current}
                                  parentLevelAnnotators={node.folder_annotators.parent}
                                  allUsers={allUsers}
                                  isAdmin={isAdmin}
                                  isAdminMode={isAdminMode}
                                  onAnnotatorsChange={(user, isAdd) => handleAnnotatorsChange(node, user, isAdd)}
                                />
                              )}
                            </Box>
                        ))}
                </Box>

                {/* Add Recently Used Files section */}
                <Box sx={{ mt: 6, mb: 3 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
                        Recently Used Files
                    </Typography>
                    <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                      <FileTable
                        items={getSortedRecentFiles()}
                        sortConfig={recentFilesSortConfig}
                        handleSort={handleRecentFilesSort}
                        handleFolderClick={handleFolderClick}
                        handleSheetClick={handleSheetClick}
                        allUsers={allUsers}
                        onAnnotatorsChange={handleAnnotatorsChange}
                        handleSheetDelete={handleSheetDelete}
                        handleSheetRename={handleSheetRename}
                        handleFolderDelete={handleFolderDelete}
                        handleFolderRename={handleFolderRename}
                        rootNode={rootNode}
                        currentPath={[]}
                      />
                    </Box>
                </Box>
            </Box>
        );
    };

    // Render files in table view when inside a folder
    const renderFilesTable = () => {
        // Get sorted items
        const sortedItems = currentNode ? getSortedItems(Object.entries(currentNode.children)) : [];

        return (
            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
              <FileTable
                items={sortedItems}
                sortConfig={sortConfig}
                handleSort={handleSort}
                handleFolderClick={handleFolderClick}
                handleSheetClick={handleSheetClick}
                allUsers={allUsers}
                onAnnotatorsChange={handleAnnotatorsChange}
                handleSheetDelete={handleSheetDelete}
                handleSheetRename={handleSheetRename}
                handleFolderDelete={handleFolderDelete}
                handleFolderRename={handleFolderRename}
                rootNode={rootNode}
                currentPath={path}
              />
            </Box>
        );
    };

    // Handle sort function for Recently Used Files
    const handleRecentFilesSort = (key) => {
        let direction = 'asc';
        if (recentFilesSortConfig.key === key && recentFilesSortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setRecentFilesSortConfig({ key, direction });
    };

    // If loading, show loading indicator
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
            {actionLoading && <LoadingModal message={actionMessage} />}
            {!searchTerm && path.length > 0 && (
                <Box sx={{ mb: 3, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                    <Typography
                        sx={{
                            fontWeight: "medium",
                            color: theme.palette.primary.main,
                            cursor: "pointer",
                            fontSize: "1rem"
                        }}
                        onClick={() => setPath([])}
                    >
                        All Folders
                    </Typography>
                    <Typography sx={{ color: "#999", fontSize: "1rem" }}>&gt;</Typography>
                    {path.map((folder, index) => (
                        <React.Fragment key={index}>
                            {index > 0 && <Typography sx={{ color: "#999", fontSize: "1rem" }}>/</Typography>}
                            <Typography
                                sx={{
                                    fontWeight: "medium",
                                    color: index === path.length - 1 ? "#333" : theme.palette.primary.main,
                                    cursor: index === path.length - 1 ? "default" : "pointer",
                                    fontSize: "1rem"
                                }}
                                onClick={() => {
                                    if (index !== path.length - 1) {
                                        setPath(path.slice(0, index + 1));
                                    }
                                }}
                            >
                                {folder}
                            </Typography>
                        </React.Fragment>
                    ))}
                </Box>
            )}
            {isInsideFolder && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <strong>{fileCount}</strong> files ({totalFileCount} total) ·{" "}
                    <strong>{folderCount}</strong> folders ({totalFolderCount} total)
                </Typography>
                )}
            {searchTerm ? renderSearchResults() : isInsideFolder ? renderFilesTable() : renderFoldersGrid()}
        </Box>
    );
});

export default DriveFolder;
